import { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, Bed, Building2, Info, MapPin, Plus, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AIParseDialog } from "@/components/AIParseDialog";
import { ConflictDialog } from "@/components/ConflictDialog";
import { UpgradeModal } from "@/components/UpgradeModal";
import { SemanticFeatureChips } from "@/components/SemanticFeatureChips";
import { ExclusiveAtsChip, StatusChip } from "@/components/StatusChip";
import { DOMBadge } from "@/components/DOMBadge";
import { SkeletonGrid } from "@/components/Skeleton";
import { formatDateShort, formatPhp } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function InventoryPage() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/listings/mine");
      setListings(data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load listings"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeCount = useMemo(
    () => listings.filter((l) => l.status === "Active").length,
    [listings]
  );

  const trialExpired = user?.trial?.expired && user?.trial?.plan !== "pro";

  const handleSaved = (created) => {
    setListings((prev) => [created, ...prev]);
  };

  const handleArchive = async (id) => {
    try {
      const { data } = await api.post(`/listings/${id}/archive`);
      setListings((prev) => prev.map((l) => (l.id === id ? data : l)));
      toast.success("Listing archived.");
    } catch (e) {
      toast.error(formatApiError(e, "Archive failed"));
    }
  };

  const openAddDialog = () => {
    if (trialExpired) {
      setShowUpgrade(true);
      return;
    }
    setDialogOpen(true);
  };

  return (
    <section
      className="mx-auto w-full max-w-7xl px-6 py-12 md:px-10 md:py-16 mb-rise"
      data-testid="page-inventory"
    >
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#1B2B4B]">
            Inventory
          </div>
          <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Your properties
          </h1>
          <p
            className="mt-3 max-w-xl text-base leading-relaxed text-gray-500"
            data-testid="inventory-count"
          >
            {loading
              ? "Loading…"
              : `${activeCount} active · ${listings.length} total`}
          </p>
        </div>
        <Button
          type="button"
          onClick={openAddDialog}
          className="mb-focus-ring h-11 rounded-xl bg-[#1B2B4B] px-6 text-sm font-semibold text-white hover:bg-[#14213A]"
          data-testid="add-listing-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add listing
        </Button>
      </div>

      {(() => {
        if (loading) {
          return (
            <div className="mt-10" data-testid="inventory-loading-skeleton">
              <SkeletonGrid count={6} />
            </div>
          );
        }
        if (listings.length === 0) {
          return (
            <div
              className="mt-10 rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-mb-card"
              data-testid="inventory-empty-state"
            >
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#1B2B4B]/10">
                <Sparkles className="h-6 w-6 text-[#1B2B4B]" />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-gray-900">
                No listings yet.
              </h3>
              <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
                Paste one of your typical write-ups and MatchBroker will structure it for you.
              </p>
              <Button
                type="button"
                onClick={openAddDialog}
                className="mb-focus-ring mt-6 h-11 rounded-xl bg-[#1B2B4B] px-6 text-sm font-semibold text-white hover:bg-[#14213A]"
                data-testid="inventory-empty-add-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add your first listing
              </Button>
            </div>
          );
        }
        return (
        <ul
          className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 md:gap-8"
          data-testid="inventory-grid"
        >
          {listings.map((l) => (
            <li
              key={l.id}
              className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-mb-card transition hover:-translate-y-[1px] hover:shadow-mb-card-hover"
              data-testid={`listing-card-${l.id}`}
            >
              <div className="flex items-start gap-2">
                <Building2 className="mt-0.5 h-5 w-5 flex-none text-gray-400" />
                <div className="min-w-0 flex-1">
                  <h3
                    className="truncate text-lg font-semibold text-gray-900"
                    data-testid={`listing-title-${l.id}`}
                  >
                    {l.title}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {l.building_or_project}
                    {l.unit_identifier ? ` · ${l.unit_identifier}` : ""}
                  </p>
                </div>
              </div>

              <div
                className="text-2xl font-bold tracking-tight text-gray-900"
                data-testid={`listing-price-${l.id}`}
              >
                {formatPhp(l.price_gross ?? l.price)}
                {l.transaction_type === "For Lease" && (
                  <span className="ml-1 text-sm font-medium text-gray-500">
                    /month
                  </span>
                )}
              </div>
              {l.price_per_sqm != null && (
                <div
                  className="-mt-2 text-xs font-medium text-gray-500"
                  data-testid={`listing-price-per-sqm-${l.id}`}
                >
                  {formatPhp(l.price_per_sqm)} / sqm
                </div>
              )}

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-gray-600"
                  data-testid={`listing-transaction-${l.id}`}
                >
                  {l.transaction_type || "For Sale"}
                </span>
                <span
                  className="text-xs font-medium text-gray-700"
                  data-testid={`listing-category-${l.id}`}
                >
                  {l.property_category || l.property_type}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  {l.location}
                </span>
                {l.bedrooms > 0 && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span className="inline-flex items-center gap-1.5">
                      <Bed className="h-4 w-4 text-gray-400" />
                      {l.bedrooms} BR
                    </span>
                  </>
                )}
                {l.floor_area_sqm != null && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span>{l.floor_area_sqm} sqm FA</span>
                  </>
                )}
                {l.lot_area_sqm != null && l.floor_area_sqm == null && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span>{l.lot_area_sqm} sqm lot</span>
                  </>
                )}
                {l.parking_slots != null && l.parking_slots > 0 && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span>{l.parking_slots} parking</span>
                  </>
                )}
              </div>

              {l.semantic_features && (
                <SemanticFeatureChips
                  value={l.semantic_features}
                  limit={6}
                  variant="muted"
                  testId={`listing-semantic-features-${l.id}`}
                  keyPrefix={`${l.id}-sf`}
                />
              )}

              {l.duplicate_warning && (
                <div
                  className="flex items-start gap-2 rounded-lg border border-brand-navy/15 bg-brand-navy-soft px-3 py-2 text-xs font-medium text-brand-navy"
                  data-testid={`listing-duplicate-warning-${l.id}`}
                >
                  <Info className="mt-0.5 h-3.5 w-3.5 flex-none" />
                  Another broker also carries this unit (non-exclusive).
                </div>
              )}

              <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
                <StatusChip status={l.status} />
                {l.exclusive_ats && <ExclusiveAtsChip />}
                <DOMBadge
                  createdAt={l.created_at}
                  testId={`listing-dom-${l.id}`}
                />
                <span className="ml-auto text-xs text-gray-400">
                  {formatDateShort(l.created_at)}
                </span>
              </div>

              {l.status === "Active" && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleArchive(l.id)}
                  className="mb-focus-ring h-10 rounded-xl border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  data-testid={`archive-listing-button-${l.id}`}
                >
                  <Archive className="mr-2 h-3.5 w-3.5" />
                  Archive
                </Button>
              )}
            </li>
          ))}
        </ul>
        );
      })()}

      <AIParseDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode="listing"
        onSaved={handleSaved}
        onConflict={setConflict}
        onUpgradeRequired={() => setShowUpgrade(true)}
      />
      <ConflictDialog
        conflict={conflict}
        onOpenChange={(open) => {
          if (!open) setConflict(null);
          if (!open && conflict?.kind === "EXCLUSIVE_ATS_CONFLICT") load();
        }}
      />
      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} />
    </section>
  );
}
